// Authenticated fetch wrapper. Use this for any request that needs the
// signed-in user's identity — i.e. anything the BE protects with
// authenticateLogin.
//
// Why this exists: the staging FE (jli-portal-fe-staging.onrender.com)
// is cross-site to the BE (jli-portal-be-staging.onrender.com) under
// the public-suffix-list rule, so Safari/Brave/Firefox-strict block the
// session cookie regardless of SameSite/Secure flags. The BE's login
// response now includes a `sessionToken` (the same value the cookie
// would carry, signed); we replay it via Authorization: Bearer on
// every authenticated call. On same-site prod the cookie still works
// and the bearer is a redundant belt-and-braces.
//
// Behavioural contract:
//   - Path is relative to API_BASE; pass leading slash. Absolute URLs
//     are rejected so the wrapper can't be accidentally pointed at a
//     foreign origin.
//   - credentials: "include" is always set so the cookie path keeps
//     working where it can.
//   - Authorization: Bearer is attached when localStorage has a
//     sessionToken — never overwrites a caller-provided Authorization
//     header (lets one-off flows like password change use their own
//     HMAC token). See the SESSION_TOKEN_KEY note for why localStorage.
//   - Returns the raw Response. Caller decides how to handle status,
//     parse the body, etc. — same shape as fetch().

import { API_BASE } from "./base";

// The signed session id, replayed as `Authorization: Bearer` on cross-site
// origins (staging) where the session cookie is a blocked third-party cookie.
//
// Stored in localStorage, NOT sessionStorage. On mobile (iOS Safari + the
// installed PWA) sessionStorage is scoped to the tab and gets wiped when the
// OS evicts a backgrounded app from memory — while `isLoggedIn`/`user` in
// localStorage survive. The result was: reopen the app, it looks signed in
// but has no bearer, the first authenticated call 401s, and the global 401
// handler force-logs-out to the login screen. localStorage keeps the bearer
// for as long as the login state it belongs to. It is no more exposed than
// the `user` object already in localStorage, and logout/expiry still clear it.
const SESSION_TOKEN_KEY = "sessionToken";
// Where the token used to live. Read as a fallback so a token written by the
// previous build (sessionStorage) still authenticates after this update
// ships, then migrated forward on first read. Remove after one release.
const LEGACY_SESSION_TOKEN_KEY = "sessionToken";

// Fired once when an authenticated call comes back 401 — i.e. the BE no
// longer recognises this session. App.tsx listens and runs the logout +
// redirect. An event (rather than importing logoutProcessor here) keeps
// this module dependency-free: logoutProcessor imports apiFetch, so a
// direct call would be a circular import.
export const SESSION_EXPIRED_EVENT = "portal:session-expired";

export type SessionExpiredDetail = {
    /** BE-supplied reason. "session_superseded" = the account signed in
     *  on another device and this session was evicted. Null for an
     *  ordinary expiry / unknown 401. */
    code: string | null;
};

// One-shot latch. A page can have half a dozen requests in flight when
// the session dies; without this every one of them would fire its own
// logout + navigate. Reset when a new session token is stored.
let expiryNotified = false;

const notifySessionExpired = (code: string | null) => {
    if (expiryNotified) return;
    expiryNotified = true;
    window.dispatchEvent(
        new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, { detail: { code } }),
    );
};

const handleUnauthorized = (res: Response, path: string) => {
    // The login endpoints answer 401 for their own reasons (and the user
    // isn't signed in yet) — never treat those as a session death.
    if (path.startsWith("/login")) return;
    // Nothing to sign out of. Guards the case where a stale poll lands
    // after logout has already cleared local state.
    try {
        if (window.localStorage.getItem("isLoggedIn") !== "true") return;
    } catch { return; }

    // clone() so the caller still gets an unread body — every existing
    // 401 call-site is free to keep parsing it.
    res.clone().json()
        .then(body => notifySessionExpired(typeof body?.code === "string" ? body.code : null))
        .catch(() => notifySessionExpired(null));
};

// Fired when an access-controlled call comes back 403 with code "no_access"
// or "read_only" (ACCESS_CONTROL_PLAN.md §7.2). AccessContext listens and
// refetches /access-control/my immediately, bypassing its route-change
// throttle — the cached pages just proved stale (a rule changed, or the
// FE's copy of the resolved access never matched the BE's in the first
// place). This is NOT a session-death signal: 401 still owns that.
export const ACCESS_DENIED_EVENT = "portal:access-denied";

export type AccessDeniedDetail = {
    code: string;
    key: string | null;
    path: string;
};

// Fired when the BE's IP allow-list gate refuses the request: 403 with
// code "ip_not_allowed" (app/admin/middleware/ipAllowlist.js). Every call
// from an unapproved network gets this, signed in or not, so App swaps
// the whole UI for the NetworkBlocked screen instead of leaving a page
// full of silently failing fetches. The detail carries the IP the BE
// saw — the one thing an admin needs in order to add it.
export const NETWORK_BLOCKED_EVENT = "portal:network-blocked";

export type NetworkBlockedDetail = {
    ip: string | null;
    kind: string | null;
    path: string;
};

const handleForbidden = (res: Response, path: string) => {
    // clone() so the caller still gets an unread body — callers that show
    // their own { message } error keep working unchanged.
    res.clone().json()
        .then(body => {
            const code = body?.code;
            if (code === "ip_not_allowed") {
                window.dispatchEvent(
                    new CustomEvent<NetworkBlockedDetail>(NETWORK_BLOCKED_EVENT, {
                        detail: {
                            ip: typeof body?.ip === "string" ? body.ip : null,
                            kind: typeof body?.kind === "string" ? body.kind : null,
                            path,
                        },
                    }),
                );
                return;
            }
            if (code !== "no_access" && code !== "read_only") return;
            window.dispatchEvent(
                new CustomEvent<AccessDeniedDetail>(ACCESS_DENIED_EVENT, {
                    detail: { code, key: typeof body?.key === "string" ? body.key : null, path },
                }),
            );
        })
        .catch(() => { /* not JSON, or no body — nothing to act on */ });
};

export const getSessionToken = (): string | null => {
    try {
        const current = window.localStorage.getItem(SESSION_TOKEN_KEY);
        if (current !== null) {
            // localStorage is authoritative now — never leave a stale copy in
            // the old location once we have a live one here.
            try { window.sessionStorage.removeItem(LEGACY_SESSION_TOKEN_KEY); } catch { /* ignore */ }
            return current;
        }
        // One-time migration from the old sessionStorage location, so a user
        // whose tab is still open when this build ships keeps their session.
        const legacy = window.sessionStorage.getItem(LEGACY_SESSION_TOKEN_KEY);
        if (legacy !== null) {
            try {
                window.localStorage.setItem(SESSION_TOKEN_KEY, legacy);
                window.sessionStorage.removeItem(LEGACY_SESSION_TOKEN_KEY);
            } catch { /* keep serving the legacy value below */ }
            return legacy;
        }
        return null;
    } catch {
        return null;
    }
};

export const setSessionToken = (token: string): void => {
    // A fresh token means a fresh session — re-arm the expiry latch so a
    // later eviction of THIS session is reported too.
    expiryNotified = false;
    try {
        window.localStorage.setItem(SESSION_TOKEN_KEY, token);
    } catch { /* storage disabled — fall back to cookie path silently */ }
    // Never leave a stale copy in the old location to be picked up later.
    try { window.sessionStorage.removeItem(LEGACY_SESSION_TOKEN_KEY); } catch { /* ignore */ }
};

export const clearSessionToken = (): void => {
    try {
        window.localStorage.removeItem(SESSION_TOKEN_KEY);
    } catch { /* ignore */ }
    try {
        window.sessionStorage.removeItem(LEGACY_SESSION_TOKEN_KEY);
    } catch { /* ignore */ }
};

export const apiFetch = (path: string, init: RequestInit = {}): Promise<Response> => {
    let resolved: string;
    if (/^https?:\/\//i.test(path)) {
        // Tolerate absolute URLs that target our own BE — legacy callers
        // built `${API_BASE}/foo` strings (sometimes via `new URL()` for
        // query construction). Anything pointing at a foreign origin is
        // rejected so this wrapper can't accidentally leak the bearer
        // off-host.
        if (!path.startsWith(API_BASE)) {
            return Promise.reject(new Error("apiFetch: external URLs not allowed"));
        }
        resolved = path;
    } else {
        resolved = `${API_BASE}${path}`;
    }

    const headers = new Headers(init.headers || {});
    if (!headers.has("Authorization")) {
        const token = getSessionToken();
        if (token) headers.set("Authorization", `Bearer ${token}`);
    }

    return fetch(resolved, {
        ...init,
        credentials: init.credentials ?? "include",
        headers,
    }).then(res => {
        // Central session-death handling. Individual call-sites still
        // do their own `if (res.status === 401)` redirect where they
        // have one — this catches the many that silently swallow it
        // (polled badge counts, dashboard KPIs) and would otherwise sit
        // on a dead session showing frozen data.
        if (res.status === 401) handleUnauthorized(res, path);
        if (res.status === 403) handleForbidden(res, path);
        return res;
    });
};
